import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Skeleton,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import {
    CalendarMonthOutlined,
    CallOutlined,
    CheckCircleOutline,
    EventAvailableOutlined,
    EventBusyOutlined,
    GroupsOutlined,
    InsightsOutlined,
    RefreshOutlined,
    EventRepeatOutlined,
    ScheduleOutlined,
    TrendingUpOutlined,
    AssignmentLateOutlined,
} from '@mui/icons-material';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { OnboardingDashboard } from '../../../Models/Widget/OnboardingPerformanceDashboard.model';
import { ProgramTypeEnum } from '../../../Enum/ProgramTypeEnum';
import { fetchOnboardingDashboard } from '../../Widgets/OnboardingWidgetService';
import { IOnboarderProvider } from '../../../Models/AdminOnboarding/OnboarderGoal.model';
import { getOnboarderProviders } from '../../AdminOnboarding/OnboarderGoals/OnboardingGoalService';
import { useNavigate } from 'react-router-dom';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateView } from '@mui/x-date-pickers/models';
import Authorization from '../../../utils/Authorization';

export enum OnboardingDashboardCardType {
    MonthlyGoal = 'MonthlyGoal',
    CompletedMonthToDate = 'CompletedMonthToDate',
    NeededToday = 'NeededToday',
    NeededForRestOfMonth = 'NeededForRestOfMonth',
    ScheduledToday = 'ScheduledToday',
    ConversionRateMonthToDate = 'ConversionRateMonthToDate',
    UpcomingAppointmentsThisMonth = 'UpcomingAppointmentsThisMonth',
    UpcomingAppointmentsNextMonth = 'UpcomingAppointmentsNextMonth',
    RescheduledThisMonth = 'RescheduledThisMonth',
    NeedsReschedule = 'NeedsReschedule',
    CallsMadeToday = 'CallsMadeToday',
    UndocumentedOnboards = 'UndocumentedOnboards',
}

interface DashboardCardProps {
    title: string;
    value: string | number;
    description: string;
    icon: ReactNode;
    cardType: OnboardingDashboardCardType;
    onClick?: (cardType: OnboardingDashboardCardType) => void;
    accentColor?: string;
    backgroundColor?: string;
    footer?: ReactNode;
}

interface NotificationState {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

const programOptions = [
    {
        value: ProgramTypeEnum.All,
        label: 'All',
    },
    {
        value: ProgramTypeEnum.FullProgram,
        label: 'Full Program',
    },
    {
        value: ProgramTypeEnum.FUHFUM,
        label: 'FUHFUM',
    },
    {
        value: ProgramTypeEnum.HRA,
        label: 'HRA',
    },
    {
        value: ProgramTypeEnum.andNext,
        label: 'andNext',
    },
];

const getErrorMessage = (error: unknown): string => {
    if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error
    ) {
        const axiosError = error as {
            response?: {
                data?: {
                    message?: string;
                    Message?: string;
                };
            };
        };

        return (
            axiosError.response?.data?.message ??
            axiosError.response?.data?.Message ??
            'Unable to load the onboarding dashboard.'
        );
    }

    return 'Unable to load the onboarding dashboard.';
};

const DashboardCard = ({
    title,
    value,
    description,
    icon,
    cardType,
    onClick,
    accentColor = '#17616b',
    backgroundColor = '#ffffff',
    footer,
}: DashboardCardProps) => {
    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: backgroundColor,
                overflow: 'hidden',
                transition: 'transform 150ms ease, box-shadow 150ms ease',
                '&:hover': {
                    transform: onClick ? 'translateY(-2px)' : undefined,
                    boxShadow: onClick ? 3 : undefined,
                },
            }}
        >
            <CardActionArea
                disabled={!onClick}
                onClick={() => onClick?.(cardType)}
                sx={{
                    height: '100%',
                    alignItems: 'stretch',
                }}
            >
                <CardContent
                    sx={{
                        height: '100%',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 700,
                                    letterSpacing: 1.2,
                                }}
                            >
                                {title}
                            </Typography>

                            <Typography
                                variant="h4"
                                sx={{
                                    mt: 0.5,
                                    fontWeight: 800,
                                    color: 'text.primary',
                                }}
                            >
                                {value}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${accentColor}18`,
                                color: accentColor,
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </Box>
                    </Box>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 1.5,
                            minHeight: 40,
                        }}
                    >
                        {description}
                    </Typography>

                    {footer && (
                        <Box sx={{ mt: 'auto', pt: 2 }}>
                            {footer}
                        </Box>
                    )}

                    {onClick && (
                        <Typography
                            variant="caption"
                            sx={{
                                mt: footer ? 1 : 'auto',
                                pt: footer ? 0 : 2,
                                color: accentColor,
                                fontWeight: 700,
                            }}
                        >
                            View details
                        </Typography>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

const DashboardCardSkeleton = () => {
    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: '#ffffff',
            }}
        >
            <CardContent
                sx={{
                    height: '100%',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Skeleton
                            animation="wave"
                            variant="text"
                            width="55%"
                            height={24}
                        />

                        <Skeleton
                            animation="wave"
                            variant="text"
                            width="35%"
                            height={48}
                            sx={{ mt: 0.5 }}
                        />
                    </Box>

                    <Skeleton
                        animation="wave"
                        variant="rounded"
                        width={44}
                        height={44}
                    />
                </Box>

                <Box sx={{ mt: 1.5 }}>
                    <Skeleton
                        animation="wave"
                        variant="text"
                        width="95%"
                        height={20}
                    />

                    <Skeleton
                        animation="wave"
                        variant="text"
                        width="75%"
                        height={20}
                    />
                </Box>

                <Skeleton
                    animation="wave"
                    variant="text"
                    width={75}
                    height={20}
                    sx={{ mt: 2 }}
                />
            </CardContent>
        </Card>
    );
};

const DashboardSkeleton = () => {
    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Skeleton
                    animation="wave"
                    variant="text"
                    width={220}
                    height={36}
                />

                <Skeleton
                    animation="wave"
                    variant="text"
                    width={320}
                    height={24}
                />
            </Box>

            <Grid container spacing={2}>
                {Array.from({ length: 12 }).map((_, index) => (
                    <Grid
                        key={index}
                        size={{
                            xs: 12,
                            sm: 6,
                            lg: 3,
                        }}
                    >
                        <DashboardCardSkeleton />
                    </Grid>
                ))}
            </Grid>
        </>
    );
};

const getProviderName = (
    provider?: IOnboarderProvider
): string => {
    if (!provider) {
        return '';
    }

    return `${provider.FirstName ?? ''} ${provider.LastName ?? ''}`.trim();
};

const OnboardingPerformanceDashboard = (props) => {
    const history = useNavigate();
    const authData = new Authorization();
    const [isProviderInitialized, setIsProviderInitialized] = useState(false);
    const [providers, setProviders] = useState<IOnboarderProvider[]>([]);
    const [dashboard, setDashboard] = useState<OnboardingDashboard>(new OnboardingDashboard());

    const {
        onboardingProviderId,
        setOnboardingProviderId,
        onboardingProgramType,
        setOnboardingProgramType,
        setOnboardingCardType,
        onboardingSelectedYear,
        setOnboardingSelectedYear,
        onboardingSelectedMonth,
        setOnboardingSelectedMonth,
    } = useWidgetContext();

    const monthLabel = (year: number, month: number) =>
        new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const selectedMonthLabel = monthLabel(onboardingSelectedYear, onboardingSelectedMonth);
    const previousMonthLabel = monthLabel(onboardingSelectedYear, onboardingSelectedMonth - 1);
    const schedulingInfo = dashboard.MonthlyGoal <= dashboard.CompletedMonthToDate
        ? 'The monthly goal is already met. No additional appointments are needed.'
        : dashboard.SchedulingConversionYear != null && dashboard.SchedulingConversionMonth != null && dashboard.SchedulingConversionRate != null
            ? dashboard.SchedulingConversionYear !== onboardingSelectedYear || dashboard.SchedulingConversionMonth !== onboardingSelectedMonth
                ? 'Using ' + monthLabel(dashboard.SchedulingConversionYear, dashboard.SchedulingConversionMonth) +
                    ' conversion rate (' + dashboard.SchedulingConversionRate + '%) because ' + selectedMonthLabel + ' conversion is zero or unavailable.'
                : 'Using ' + selectedMonthLabel + ' MTD conversion rate (' + dashboard.SchedulingConversionRate + '%). Estimates subtract distinct upcoming members.'
            : 'To Schedule is unavailable because conversion rates for both ' + selectedMonthLabel + ' and ' + previousMonthLabel + ' are zero or unavailable.';

    const [pickerDate, setPickerDate] = useState(
        new Date(
            onboardingSelectedYear,
            onboardingSelectedMonth - 1,
            1
        )
    );

    const [pickerView, setPickerView] = useState<DateView>('month');

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const loadProviders = useCallback(async () => {
        try {
            const providerList = await getOnboarderProviders(
                'Office Onboarding',
                false
            );

            const sortedProviders = [...providerList].sort((first, second) =>
                getProviderName(first).localeCompare(getProviderName(second))
            );

            setProviders([
                ...sortedProviders,
                {
                    Id: 'others',
                    FirstName: 'Others',
                    LastName: ''
                }
            ]);
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        void loadProviders();
    }, [loadProviders]);

    useEffect(() => {
        if (!providers.length) return;
        if (onboardingProviderId) {
            setIsProviderInitialized(true);
            return;
        }

        const providerExists =
            Boolean(authData.UserId) &&
            providers.some(provider => provider.Id === authData.UserId);

        setOnboardingProviderId(
            props.hasElevatedFilterAccess
                ? 'All'
                : providerExists
                    ? authData.UserId
                    : 'All'
        );

        setIsProviderInitialized(true);
    }, [
        providers,
        authData.UserId,
        props.hasElevatedFilterAccess,
        onboardingProviderId,
    ]);

    const [notification, setNotification] =
        useState<NotificationState>({
            open: false,
            message: '',
            severity: 'error',
        });

    const currentMonthName = useMemo(() => {
        return new Date(
            onboardingSelectedYear,
            onboardingSelectedMonth - 1,
            1
        ).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        });
    }, [
        onboardingSelectedYear,
        onboardingSelectedMonth,
    ]);

    const nextMonthName = useMemo(() => {
        const date = new Date(
            onboardingSelectedYear,
            onboardingSelectedMonth,
            1
        );

        return date.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        });
    }, [
        onboardingSelectedYear,
        onboardingSelectedMonth,
    ]);

    const loadDashboard = useCallback(async () => {
        setIsLoading(true);

        try {
            const result = await fetchOnboardingDashboard(
                onboardingProviderId,
                onboardingProgramType,
                onboardingSelectedYear,
                onboardingSelectedMonth
            );

            setDashboard({
                ...new OnboardingDashboard(),
                ...result,
            });
        } catch (error) {
            console.error('Unable to load onboarding dashboard:', error);

            setDashboard(new OnboardingDashboard());

            setNotification({
                open: true,
                message: getErrorMessage(error),
                severity: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        onboardingProviderId,
        onboardingProgramType,
        onboardingSelectedYear,
        onboardingSelectedMonth,
    ]);

    useEffect(() => {
        setIsLoading(true);
        if (!isProviderInitialized) return;

        void loadDashboard();
    }, [isProviderInitialized, loadDashboard]);

    const handleProviderChange = (
        event: SelectChangeEvent<string>
    ) => {
        setOnboardingProviderId(
            event.target.value
        );
    };

    const handleProgramChange = (
        event: SelectChangeEvent<ProgramTypeEnum>
    ) => {
        setOnboardingProgramType(
            event.target.value as ProgramTypeEnum
        );
    };

    const conversionRate = Number(
        dashboard.ConversionRateMonthToDate ?? 0
    );

    const handleCardClick = (
        cardType: OnboardingDashboardCardType
    ) => {
        setOnboardingCardType(cardType);
        history(`/provider/metricsummary/onboardingperformancedetail?hasElevatedFilterAccess=${props.hasElevatedFilterAccess}`);
    };

    return (
        <Grid size={{ xs: 12, lg: 12 }}>
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                }}
            >
                <Grid container spacing={2}>
                    <Box
                        sx={{
                            minHeight: '100vh',
                            width: '100%',
                            bgcolor: '#f4f7f8',
                            p: {
                                xs: 2,
                                md: 3,
                            },
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3,
                                },
                                mb: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                alignItems="center"
                            >
                                <Grid
                                    size={{
                                        xs: 12,
                                        lg: 5,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#e5f1f3',
                                                color: '#17616b',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <InsightsOutlined />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 800,
                                                }}
                                            >
                                                Onboarding Performance Dashboard
                                            </Typography>

                                            <Typography
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                }}
                                            >
                                                Track goals, completed onboards,
                                                appointments, and call activity.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid
                                    size={{
                                        xs: 12,
                                        lg: 7,
                                    }}
                                >
                                    <Grid
                                        container
                                        spacing={1.5}
                                        justifyContent={{
                                            xs: 'flex-start',
                                            lg: 'flex-end',
                                        }}
                                    >
                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 3,
                                            }}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={
                                                    AdapterDateFns
                                                }
                                            >
                                                <DatePicker
                                                    label="Month"
                                                    views={[
                                                        'year',
                                                        'month',
                                                    ]}
                                                    openTo="month"
                                                    value={
                                                        pickerDate
                                                    }
                                                    onViewChange={
                                                        view =>
                                                            setPickerView(
                                                                view
                                                            )
                                                    }
                                                    onChange={
                                                        value => {
                                                            if (!value) {
                                                                return;
                                                            }

                                                            setPickerDate(
                                                                value
                                                            );

                                                            if (
                                                                pickerView !==
                                                                'month'
                                                            ) {
                                                                return;
                                                            }

                                                            setOnboardingSelectedYear(
                                                                value.getFullYear()
                                                            );

                                                            setOnboardingSelectedMonth(
                                                                value.getMonth() +
                                                                1
                                                            );
                                                        }
                                                    }
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth:
                                                                true,
                                                            size: 'small',
                                                        },
                                                        actionBar: {
                                                            actions: [],
                                                        },
                                                        popper: {
                                                            sx: {
                                                                '& .MuiDateCalendar-root':
                                                                {
                                                                    height:
                                                                        'auto',
                                                                    minHeight: 0,
                                                                },
                                                                '& .MuiPickersSlideTransition-root':
                                                                {
                                                                    minHeight:
                                                                        'auto',
                                                                },
                                                                '& .MuiMonthCalendar-root':
                                                                {
                                                                    height:
                                                                        'auto',
                                                                    minHeight: 0,
                                                                    pb: 1,
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Grid>

                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 3,
                                            }}
                                        >
                                            <FormControl
                                                fullWidth
                                                size="small"
                                            >
                                                <InputLabel>
                                                    Program
                                                </InputLabel>

                                                <Select
                                                    value={
                                                        onboardingProgramType
                                                    }
                                                    label="Program"
                                                    onChange={
                                                        handleProgramChange
                                                    }
                                                >
                                                    {programOptions.map(
                                                        option => (
                                                            <MenuItem
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {
                                                                    option.label
                                                                }
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 3,
                                            }}
                                        >
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                disabled={!props.hasElevatedFilterAccess}
                                            >
                                                <InputLabel>
                                                    Onboarder
                                                </InputLabel>

                                                <Select
                                                    value={
                                                        onboardingProviderId
                                                    }
                                                    label="Onboarder"
                                                    onChange={
                                                        handleProviderChange
                                                    }
                                                >
                                                    <MenuItem value="All">
                                                        All
                                                    </MenuItem>

                                                    {providers.map(
                                                        provider => (
                                                            <MenuItem
                                                                key={
                                                                    provider.Id
                                                                }
                                                                value={
                                                                    provider.Id
                                                                }
                                                            >
                                                                {
                                                                    provider.FirstName
                                                                }{' '}
                                                                {
                                                                    provider.LastName
                                                                }
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 1,
                                            }}
                                        >
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() =>
                                                    void loadDashboard()
                                                }
                                                disabled={
                                                    isLoading
                                                }
                                                sx={{
                                                    minWidth: 48,
                                                    height: 40,
                                                }}
                                            >
                                                <RefreshOutlined />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>

                        {isLoading ? (
                            <DashboardSkeleton />
                        ) : (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        {currentMonthName}{' '}
                                        overview
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Counts reflect the currently
                                        selected program and filters.
                                    </Typography>
                                </Box>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Monthly Goal"
                                            value={
                                                dashboard.MonthlyGoal
                                            }
                                            description="Total onboarding goal for the selected program this calendar month."
                                            icon={
                                                <CalendarMonthOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.MonthlyGoal
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#17616b"
                                            backgroundColor="#f7fbfb"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Completed MTD"
                                            value={
                                                dashboard.CompletedMonthToDate
                                            }
                                            description="Onboards completed from the beginning of the month through today."
                                            icon={
                                                <CheckCircleOutline />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.CompletedMonthToDate
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#2e7d32"
                                            backgroundColor="#f7fbf7"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Needed Today"
                                            value={
                                                dashboard.NeededToday
                                            }
                                            description="Onboards required today to maintain the pace needed to achieve the monthly goal."
                                            icon={
                                                <TrendingUpOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.NeededToday
                                            }
                                            accentColor="#ed6c02"
                                            backgroundColor="#fffaf5"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="To Schedule"
                                            value={
                                                dashboard.NeededForRestOfMonth ?? 'N/A'
                                            }
                                            description={schedulingInfo}
                                            icon={
                                                <ScheduleOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.NeededForRestOfMonth
                                            }
                                            accentColor="#7b1fa2"
                                            backgroundColor="#fbf7fd"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Scheduled Today"
                                            value={
                                                dashboard.ScheduledToday
                                            }
                                            description="Onboarding appointments created today."
                                            icon={
                                                <EventAvailableOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.ScheduledToday
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#1565c0"
                                            backgroundColor="#f5f9ff"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Conversion Rate MTD"
                                            value={`${conversionRate.toFixed(
                                                2
                                            )}%`}
                                            description="MTD successful onboardings ÷ total outcomes (successful, cancelled/no-show, and refused)."
                                            icon={
                                                <InsightsOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.ConversionRateMonthToDate
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#00838f"
                                            backgroundColor="#f4fbfc"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Upcoming This Month"
                                            value={
                                                dashboard.UpcomingAppointmentsThisMonth
                                            }
                                            description={`Upcoming onboarding appointments for ${currentMonthName}.`}
                                            icon={
                                                <GroupsOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.UpcomingAppointmentsThisMonth
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#3949ab"
                                            backgroundColor="#f7f8fe"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Upcoming Next Month"
                                            value={
                                                dashboard.UpcomingAppointmentsNextMonth
                                            }
                                            description={`Upcoming onboarding appointments for ${nextMonthName}.`}
                                            icon={
                                                <CalendarMonthOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.UpcomingAppointmentsNextMonth
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#6a1b9a"
                                            backgroundColor="#fbf7fd"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Rescheduled This Month"
                                            value={
                                                dashboard.RescheduledThisMonth
                                            }
                                            description="Cancelled or no-show appointments that were successfully rescheduled this month."
                                            icon={
                                                <EventRepeatOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.RescheduledThisMonth
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#5e35b1"
                                            backgroundColor="#faf7fe"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Needs Reschedule"
                                            value={
                                                dashboard.NeedsReschedule
                                            }
                                            description="Cancelled and no-show appointments that still require a new appointment."
                                            icon={
                                                <EventBusyOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.NeedsReschedule
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#d32f2f"
                                            backgroundColor="#fff7f7"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Calls Made Today"
                                            value={
                                                dashboard.CallsMadeToday
                                            }
                                            description="Total onboarding calls made today across the selected onboarders."
                                            icon={
                                                <CallOutlined />
                                            }
                                            cardType={
                                                OnboardingDashboardCardType.CallsMadeToday
                                            }
                                            onClick={
                                                handleCardClick
                                            }
                                            accentColor="#0277bd"
                                            backgroundColor="#f5faff"
                                        />
                                    </Grid>

                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 6,
                                            lg: 3,
                                        }}
                                    >
                                        <DashboardCard
                                            title="Undocumented Onboards"
                                            value={dashboard.UndocumentedOnboards}
                                            description="Undocumented onboarding appointments from the last 3 days."
                                            icon={<AssignmentLateOutlined />}
                                            cardType={OnboardingDashboardCardType.UndocumentedOnboards}
                                            onClick={handleCardClick}
                                            accentColor="#ed6c02"
                                            backgroundColor="#fff7ed"
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        <Snackbar
                            open={
                                notification.open
                            }
                            autoHideDuration={
                                5000
                            }
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            onClose={() =>
                                setNotification(
                                    previous => ({
                                        ...previous,
                                        open: false,
                                    })
                                )
                            }
                        >
                            <Alert
                                severity={
                                    notification.severity
                                }
                                variant="filled"
                                onClose={() =>
                                    setNotification(
                                        previous => ({
                                            ...previous,
                                            open: false,
                                        })
                                    )
                                }
                            >
                                {
                                    notification.message
                                }
                            </Alert>
                        </Snackbar>
                    </Box>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default OnboardingPerformanceDashboard;